import React, { useCallback, useEffect, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import {
  OVERRIDABLE_VIEW_CAPS,
  type Permission,
  type RolePermissionCell,
  type RolePermissionMatrix,
  fetchPermissions,
  fetchRolePermissionMatrix,
  setRolePermissionOverride,
} from "../../services/adminApi";

const capLabel = (key: string): string =>
  OVERRIDABLE_VIEW_CAPS.find((c) => c.key === key)?.label ?? key;

/** A cell's 3-state select value: "" = Default (baseline), "1" = Grant, "0" = Deny. */
const cellValue = (c: RolePermissionCell): "" | "0" | "1" =>
  c.override === null ? "" : (String(c.override) as "0" | "1");

/**
 * RBAC editor: an EDITABLE grid of system roles × toggleable view capabilities
 * (migration 128). Each cell is a 3-state control — Default (fall back to the
 * migration-owned baseline) / Grant / Deny — reflecting baseline + override +
 * effective. Writes go through the per-role override endpoint on change
 * (optimistic, reverted on failure). Below the grid, the full capability
 * catalog is shown read-only for reference.
 *
 * Editing is offered only to platform users with users.manage; everyone else
 * (company admins) sees the grid read-only. Changes take effect on each
 * affected user's NEXT login (capabilities are baked into the token).
 */
export const RolesPermissionsTab: React.FC = () => {
  const { token, role, can } = useAuth();
  const canEdit = role === "platform" && can("users.manage");

  const [matrix, setMatrix] = useState<RolePermissionMatrix | null>(null);
  const [perms, setPerms] = useState<Permission[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    Promise.all([fetchRolePermissionMatrix(token), fetchPermissions(token)])
      .then(([m, p]) => {
        setMatrix(m);
        setPerms(p);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onChangeCell = async (
    roleId: number,
    permissionKey: string,
    raw: "" | "0" | "1",
  ): Promise<void> => {
    if (!token || !matrix) return;
    const effect: 0 | 1 | null = raw === "" ? null : (Number(raw) as 0 | 1);
    const prev = matrix;
    // Optimistic update: recompute the cell's effective from the new override,
    // falling back to baseline when cleared.
    setMatrix({
      ...matrix,
      roles: matrix.roles.map((r) =>
        r.role_id !== roleId
          ? r
          : {
              ...r,
              cells: r.cells.map((c) =>
                c.permission_key !== permissionKey
                  ? c
                  : {
                      ...c,
                      override: effect,
                      effective: effect === null ? c.baseline : Boolean(effect),
                    },
              ),
            },
      ),
    });
    const saveKey = `${roleId}:${permissionKey}`;
    setSavingKey(saveKey);
    setError(null);
    try {
      await setRolePermissionOverride(roleId, permissionKey, effect, token);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMatrix(prev); // revert
    } finally {
      setSavingKey(null);
    }
  };

  if (error && !matrix) {
    return <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>;
  }
  if (!matrix || !perms) return <LoadingSpinner label="loading roles" />;

  const caps = matrix.capabilities;

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-secondary-800">Role view permissions</h2>
        </div>
        <p className="text-xs text-secondary-500 mb-2">
          Toggle which tiles &amp; views each role can see. <strong>Default</strong> keeps the
          built-in baseline; <strong>Grant</strong> / <strong>Deny</strong> override it for the
          whole role. Changes take effect on each affected user&apos;s <strong>next login</strong>.
          {!canEdit && " (read-only — requires a platform users.manage account)"}
        </p>
        {error && (
          <div className="mb-2 text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
            {error}
          </div>
        )}
        <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Role</th>
                {caps.map((k) => (
                  <th key={k} className="text-left px-3 py-2 whitespace-nowrap">
                    {capLabel(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.roles.map((r) => (
                <tr key={r.role_id} className="border-t border-secondary-100">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.role_name ?? `#${r.role_id}`}</div>
                    {r.company_type && (
                      <div className="text-[11px] uppercase tracking-wide text-secondary-400">
                        {r.company_type}
                      </div>
                    )}
                  </td>
                  {caps.map((k) => {
                    const cell =
                      r.cells.find((c) => c.permission_key === k) ??
                      ({
                        permission_key: k,
                        baseline: false,
                        override: null,
                        effective: false,
                      } as RolePermissionCell);
                    const saving = savingKey === `${r.role_id}:${k}`;
                    return (
                      <td key={k} className="px-3 py-2">
                        <select
                          className="border border-secondary-300 rounded px-2 py-1 text-xs bg-white disabled:opacity-60"
                          value={cellValue(cell)}
                          disabled={!canEdit || saving}
                          onChange={(e) =>
                            void onChangeCell(r.role_id, k, e.target.value as "" | "0" | "1")
                          }
                        >
                          <option value="">
                            Default ({cell.baseline ? "on" : "off"})
                          </option>
                          <option value="1">Grant</option>
                          <option value="0">Deny</option>
                        </select>
                        <div className="text-[10px] text-secondary-400 mt-0.5">
                          {cell.effective ? "visible" : "hidden"}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-semibold text-secondary-800 mb-2">Capabilities</h2>
        <div className="bg-white border border-secondary-200 rounded-md divide-y divide-secondary-100">
          {perms.map((p) => (
            <div key={p.permission_key} className="px-3 py-2 flex items-start gap-3">
              <code className="text-xs font-mono text-secondary-800 w-40 shrink-0">
                {p.permission_key}
              </code>
              <div className="min-w-0">
                <div className="text-sm">{p.label}</div>
                {p.description && (
                  <div className="text-xs text-secondary-500">{p.description}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default RolesPermissionsTab;
